import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import axios from 'axios';
import { Row, Col, Card, Statistic, Button, Alert, Progress, Tag, Space, Typography, Spin } from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  ReloadOutlined,
  TrophyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { getHeaders } from '../../utility/getHeader';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";

const { Text } = Typography;

const PAGE_BG = "#ffffff";
const BORDER = "#f0f0f0";
const TEXT_MUTED = "#64748b";
const PALETTE = {
  teal: "#0d9488",
  sky: "#2563eb",
  violet: "#7c3aed",
  amber: "#f59e0b",
  rose: "#e11d48",
  emerald: "#059669",
};
const CHART_COLORS = [PALETTE.teal, PALETTE.sky, PALETTE.violet, PALETTE.amber, PALETTE.rose, PALETTE.emerald];

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: "#ffffff",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: "#1e293b",
  },
  labelStyle: { color: "#1e293b" },
};

const CARD_SHADOW = "0 1px 2px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.06)";

const Panel = ({ title, children, extra, style }) => (
  <Card
    title={<span style={{ fontSize: 13, letterSpacing: 0.5, color: TEXT_MUTED, textTransform: "uppercase" }}>{title}</span>}
    extra={extra}
    style={{ height: "100%", boxShadow: CARD_SHADOW, ...style }}
    styles={{ body: { paddingTop: 16 } }}
  >
    {children}
  </Card>
);

const EmptyHint = ({ text }) => (
  <div style={{ textAlign: "center", padding: "32px 0" }}>
    <Text type="secondary">{text}</Text>
  </div>
);

function AnalyticsDashboard() {
  const [dashboardData, setDashboardData] = useState({
    todaySales: 0,
    yesterdaySales: 0,
    monthlyPurchases: 0,
    todayPurchases: 0,
    totalOrders: 0,
    totalSuppliers: 0
  });

  const [monthlySalesData, setMonthlySalesData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [topProductsData, setTopProductsData] = useState([]);
  const [suppliersData, setSuppliersData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [salesExpensesData, setSalesExpensesData] = useState({
    totalSales: 0,
    totalExpenses: 0,
    netProfit: 0
  });
  const [dailySalesData, setDailySalesData] = useState([]);
  const [purchaseTrendsData, setPurchaseTrendsData] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // API Functions
  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('analytics/dashboard', getHeaders());
      if (response.data.success) {
        // MySQL returns SUM()/DECIMAL aggregates as strings; coerce to numbers so
        // Statistic/trend math below doesn't silently do string concatenation.
        const d = response.data.data || {};
        setDashboardData({
          todaySales: Number(d.todaySales) || 0,
          yesterdaySales: Number(d.yesterdaySales) || 0,
          monthlyPurchases: Number(d.monthlyPurchases) || 0,
          todayPurchases: Number(d.todayPurchases) || 0,
          totalOrders: Number(d.totalOrders) || 0,
          totalSuppliers: Number(d.totalSuppliers) || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    }
  };

  const fetchMonthlySalesData = async () => {
    try {
      const response = await axios.get('/analytics/monthly-sales-purchases', getHeaders());
      if (response.data.success) {
        // MySQL returns SUM()/DECIMAL aggregates as strings; coerce to numbers so
        // recharts can compute bar heights (it renders axes fine from string-coerced
        // domains but silently drops bars when the per-point values aren't numeric).
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          sales: Number(row.sales) || 0,
          purchases: Number(row.purchases) || 0,
        }));
        setMonthlySalesData(formatted);
      }
    } catch (error) {
      console.error('Error fetching monthly sales data:', error);
      // Keep empty array on error - chart will show empty state
    }
  };

  const fetchSuppliersData = async () => {
    try {
      const response = await axios.get('analytics/suppliers-outstanding', getHeaders());
      if (response.data.success) {
        const formattedData = response.data.data.map(supplier => ({
          name: supplier.supplier_name,
          orders: Number(supplier.total_orders) || 0,
          amount: Number(supplier.total_amount) || 0,
          rating: Number(supplier.avg_rating) || 0
        }));
        setSuppliersData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching suppliers data:', error);
      // Keep empty array on error
    }
  };

  const fetchOrderStatusData = async () => {
    try {
      const response = await axios.get('analytics/order-status', getHeaders());
      if (response.data.success) {
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          value: Number(row.value) || 0,
        }));
        setOrderStatusData(formatted);
      }
    } catch (error) {
      console.error('Error fetching order status data:', error);
      // Keep empty array on error
    }
  };

  const fetchTopProductsData = async () => {
    try {
      const response = await axios.get('analytics/top-products', getHeaders());
      if (response.data.success) {
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          sales: Number(row.sales) || 0,
          revenue: Number(row.revenue) || 0,
        }));
        setTopProductsData(formatted);
      }
    } catch (error) {
      console.error('Error fetching top products data:', error);
      // Keep empty array on error
    }
  };

  const fetchCategoryData = async () => {
    try {
      const response = await axios.get('analytics/category-distribution', getHeaders());
      if (response.data.success) {
        // MySQL returns SUM()/DECIMAL aggregates as strings; coerce to numbers so the
        // Pie chart can compute wedge angles and the running total isn't string-concatenated.
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          value: Number(row.value) || 0,
        }));
        setCategoryData(formatted);
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
      // Keep empty array on error
    }
  };

  const fetchSalesExpensesData = async () => {
    try {
      const response = await axios.get('analytics/sales-expenses', getHeaders());
      if (response.data.success) {
        const d = response.data.data || {};
        setSalesExpensesData({
          totalSales: Number(d.totalSales) || 0,
          totalExpenses: Number(d.totalExpenses) || 0,
          netProfit: Number(d.netProfit) || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching sales expenses data:', error);
      // Keep default values on error
    }
  };

  const fetchDailySalesData = async () => {
    try {
      const response = await axios.get('analytics/daily-sales-trend', getHeaders());
      if (response.data.success) {
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          sales: Number(row.sales) || 0,
        }));
        setDailySalesData(formatted);
      }
    } catch (error) {
      console.error('Error fetching daily sales data:', error);
      // Keep empty array on error
    }
  };

  const fetchPurchaseTrendsData = async () => {
    try {
      const response = await axios.get('analytics/analytics/purchase-trends', getHeaders());
      if (response.data.success) {
        const formatted = (response.data.data || []).map(row => ({
          ...row,
          purchases: Number(row.purchases) || 0,
        }));
        setPurchaseTrendsData(formatted);
      }
    } catch (error) {
      console.error('Error fetching purchase trends data:', error);
      // Keep empty array on error
    }
  };

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchDashboardData(),
        fetchMonthlySalesData(),
        fetchSuppliersData(),
        fetchOrderStatusData(),
        fetchTopProductsData(),
        fetchCategoryData(),
        fetchSalesExpensesData(),
        fetchDailySalesData(),
        fetchPurchaseTrendsData()
      ]);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to refresh dashboard data');
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await fetchAllData();
      setIsLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setError(null);
    setIsLoading(true);
    await fetchAllData();
    setIsLoading(false);
  };

  const todaySales = dashboardData.todaySales || 0;
  const yesterdaySales = dashboardData.yesterdaySales || 0;
  const salesChangePct = yesterdaySales > 0
    ? ((todaySales - yesterdaySales) / yesterdaySales) * 100
    : (todaySales > 0 ? 100 : 0);
  const isSalesUp = salesChangePct >= 0;

  const metrics = [
    {
      label: "Today's Sales",
      value: todaySales,
      prefix: "฿",
      color: PALETTE.teal,
      icon: <DollarOutlined />,
      trend: (
        <span style={{ fontSize: 12, color: isSalesUp ? PALETTE.emerald : PALETTE.rose }}>
          {isSalesUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(salesChangePct).toFixed(1)}% vs yesterday
        </span>
      ),
    },
    { label: "Monthly Purchases", value: dashboardData.monthlyPurchases, prefix: "฿", color: PALETTE.violet, icon: <ShoppingCartOutlined /> },
    { label: "Today's Purchases", value: dashboardData.todayPurchases, prefix: "฿", color: PALETTE.amber, icon: <ShoppingCartOutlined /> },
    { label: "Total Orders", value: dashboardData.totalOrders, color: PALETTE.rose, icon: <TrophyOutlined /> },
    { label: "Active Suppliers", value: dashboardData.totalSuppliers, color: PALETTE.emerald, icon: <ShopOutlined /> },
  ];

  const maxTopProductSales = Math.max(1, ...(topProductsData || []).map(p => p.sales || 0));
  const categoryTotal = (categoryData || []).reduce((sum, c) => sum + (c.value || 0), 0);

  const content = (
    <div style={{ background: PAGE_BG, minHeight: "100vh", padding: 20 }}>
      {error && (
        <Alert
          type="warning"
          message="Warning"
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Text style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", display: "block" }}>Analytics Overview</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>Here's what's happening with your store today</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={isLoading}>
            {isLoading ? "Refreshing..." : "Refresh Data"}
          </Button>
        </Col>
      </Row>

      <Spin spinning={isLoading}>
        {/* KPI Row */}
        <Row gutter={[16, 16]}>
          {metrics.map((m) => (
            <Col key={m.label} xs={12} sm={8} md={8} lg={Math.floor(24 / metrics.length)}>
              <Card style={{ boxShadow: CARD_SHADOW }} styles={{ body: { padding: 16 } }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Statistic
                    title={<span style={{ color: TEXT_MUTED, fontSize: 12 }}>{m.label}</span>}
                    value={m.value || 0}
                    prefix={m.prefix}
                    valueStyle={{ color: m.color, fontSize: 20, fontWeight: 700 }}
                  />
                  <span style={{
                    width: 36, height: 36, borderRadius: 10, background: `${m.color}1a`,
                    color: m.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>
                    {m.icon}
                  </span>
                </div>
                {m.trend && <div style={{ marginTop: 8 }}>{m.trend}</div>}
              </Card>
            </Col>
          ))}
        </Row>

        {/* Hero Row: big trend chart + summary sidebar */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Panel title="Monthly Sales vs Purchases Flow">
              {monthlySalesData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={monthlySalesData} barGap={6}>
                    <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                    <XAxis dataKey="month" stroke={TEXT_MUTED} fontSize={12} axisLine={false} tickLine={false} />
                    <YAxis stroke={TEXT_MUTED} fontSize={12} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltipStyle} />
                    <Bar dataKey="sales" fill={PALETTE.teal} name="Sales" radius={[8, 8, 0, 0]} maxBarSize={36} />
                    <Bar dataKey="purchases" fill={PALETTE.rose} name="Purchases" radius={[8, 8, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyHint text="No sales data available" />}
            </Panel>
          </Col>

          <Col xs={24} lg={8}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Panel title="Total Sales & Expenses">
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Statistic title={<span style={{ color: TEXT_MUTED }}>Total Sales</span>} value={salesExpensesData.totalSales || 0} prefix="฿" valueStyle={{ color: PALETTE.teal, fontSize: 18 }} />
                  <Statistic title={<span style={{ color: TEXT_MUTED }}>Total Expenses</span>} value={salesExpensesData.totalExpenses || 0} prefix="฿" valueStyle={{ color: PALETTE.rose, fontSize: 18 }} />
                  <Statistic title={<span style={{ color: TEXT_MUTED }}>Net Profit</span>} value={salesExpensesData.netProfit || 0} prefix="฿" valueStyle={{ color: (salesExpensesData.netProfit || 0) >= 0 ? PALETTE.emerald : PALETTE.amber, fontSize: 20, fontWeight: 700 }} />
                </Space>
              </Panel>

              <Panel title="Order Wise Status">
                {orderStatusData?.length > 0 ? (
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {orderStatusData.map((status, index) => (
                      <div key={index}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <Text style={{ color: "#1e293b" }}>{status.status}</Text>
                          <Text style={{ color: status.color }}>{status.value}%</Text>
                        </div>
                        <Progress percent={status.value} showInfo={false} strokeColor={status.color} trailColor={BORDER} />
                      </div>
                    ))}
                  </Space>
                ) : <EmptyHint text="No order status data available" />}
              </Panel>
            </Space>
          </Col>
        </Row>

        {/* Insights Row */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Panel title="Suppliers Outstanding Payment">
              {suppliersData?.length > 0 ? (
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  {suppliersData.map((supplier, index) => (
                    <div key={index} style={{ borderBottom: index !== suppliersData.length - 1 ? `1px solid ${BORDER}` : "none", paddingBottom: 10 }}>
                      <Text strong style={{ color: "#1e293b" }}>{supplier.name}</Text>
                      <div style={{ marginTop: 4 }}>
                        <Space size={6} wrap>
                          <Tag color="blue">Orders: {supplier.orders}</Tag>
                          <Tag color="gold">฿{supplier.amount?.toLocaleString() || '0'}</Tag>
                          <Tag color="green">{supplier.rating}⭐</Tag>
                        </Space>
                      </div>
                    </div>
                  ))}
                </Space>
              ) : <EmptyHint text="No supplier data available" />}
            </Panel>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Panel title="Top Selling Products">
              {topProductsData?.length > 0 ? (
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  {topProductsData.map((product, index) => (
                    <div key={index} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Tag color={CHART_COLORS[index % CHART_COLORS.length]} style={{ minWidth: 26, textAlign: "center" }}>#{index + 1}</Tag>
                      <Text style={{ color: "#1e293b", flex: 1 }}>{product.name}</Text>
                      <Text strong style={{ color: PALETTE.sky }}>{product.sales}</Text>
                    </div>
                  ))}
                </Space>
              ) : <EmptyHint text="No product data available" />}
            </Panel>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Panel title="Category Distribution">
              {categoryData?.length > 0 ? (
                <>
                  <div style={{ position: "relative" }}>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={42}
                          outerRadius={62}
                          paddingAngle={3}
                          cornerRadius={6}
                          dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} stroke="none" />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{
                      position: "absolute", top: "44%", left: "50%", transform: "translate(-50%, -50%)",
                      textAlign: "center", pointerEvents: "none",
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{categoryTotal.toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: TEXT_MUTED }}>Total</div>
                    </div>
                  </div>
                  <Space wrap size={[8, 4]} style={{ marginTop: 8 }}>
                    {categoryData.map((category, index) => (
                      <Tag key={index} color={category.color || CHART_COLORS[index % CHART_COLORS.length]}>{category.name}</Tag>
                    ))}
                  </Space>
                </>
              ) : <EmptyHint text="No category data available" />}
            </Panel>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Panel title="Customer Sources">
              {topProductsData?.length > 0 ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {topProductsData.map((product, index) => (
                    <div key={index}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <Text style={{ color: "#1e293b", fontSize: 13 }}>{product.name}</Text>
                        <Text style={{ color: product.color || CHART_COLORS[index % CHART_COLORS.length], fontSize: 13 }}>
                          ฿{product.revenue?.toLocaleString() || '0'}
                        </Text>
                      </div>
                      <Progress
                        percent={(product.sales / maxTopProductSales) * 100}
                        showInfo={false}
                        strokeColor={product.color || CHART_COLORS[index % CHART_COLORS.length]}
                        trailColor={BORDER}
                      />
                    </div>
                  ))}
                </Space>
              ) : <EmptyHint text="No customer source data available" />}
            </Panel>
          </Col>
        </Row>

        {/* Trends Row */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
            <Panel title="Daily Sales Trend">
              {dailySalesData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={dailySalesData}>
                    <Area type="monotone" dataKey="sales" stroke={PALETTE.teal} strokeWidth={2} fill={PALETTE.teal} fillOpacity={0.18} />
                    <XAxis dataKey="day" stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <YAxis stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltipStyle} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyHint text="No daily sales data available" />}
            </Panel>
          </Col>

          <Col xs={24} lg={8}>
            <Panel title="Purchase Trends">
              {purchaseTrendsData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={purchaseTrendsData}>
                    <Line
                      type="monotone"
                      dataKey="purchases"
                      stroke={PALETTE.rose}
                      strokeWidth={3}
                      dot={{ fill: "#fff", stroke: PALETTE.rose, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <XAxis dataKey="day" stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <YAxis stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltipStyle} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyHint text="No purchase trends data available" />}
            </Panel>
          </Col>

          <Col xs={24} lg={8}>
            <Panel title="Revenue Overview">
              {monthlySalesData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={monthlySalesData.slice(-6)}>
                    <Bar dataKey="sales" fill={PALETTE.sky} radius={[6, 6, 0, 0]} maxBarSize={28} />
                    <XAxis dataKey="month" stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <YAxis stroke={TEXT_MUTED} fontSize={10} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltipStyle} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyHint text="No revenue data available" />}
            </Panel>
          </Col>
        </Row>
      </Spin>
    </div>
  );

  return (
    <Layout>
      <Header title="Analytics Dashboard" />
      {content}
    </Layout>
  );
}

export default AnalyticsDashboard;
